<template>
  <UIModal
    class="ModalConfirmAction"
    :class="rootClass"
    size="lg"
    :isOpen="isOpen"
    :isDismissable="!isLoading"
    @close="emit('close')"
  >
    <template #title>{{ title }}</template>

    <template #content>
      <div class="ModalConfirmAction__body">
        <p class="ModalConfirmAction__lead">{{ lead }}</p>

        <!-- Anything the confirm needs between the lead and the banner (e.g. the
             name of the row being acted on). -->
        <slot />

        <!-- The banner is optional: a plain confirm carries its whole message in
             the lead. -->
        <UIBanner
          v-if="bannerTitle || bannerDescription"
          :theme="bannerTheme"
          :icon="bannerIcon"
          titleTag="h3"
          :title="bannerTitle"
          :description="bannerDescription"
        />
      </div>
    </template>

    <template #actions>
      <UIButton
        theme="secondary"
        :isDisabled="isLoading"
        @click.prevent="emit('close')"
      >
        {{ cancelLabel }}
      </UIButton>
      <UIButton
        :theme="confirmTheme"
        :isLoading="isLoading"
        @click.prevent="emit('confirm')"
      >
        {{ confirmLabel }}
      </UIButton>
    </template>
  </UIModal>
</template>

<script lang="ts" setup>
import { UIBanner, UIButton, UIModal, type IIcons } from '@foundever/ui-lib'

withDefaults(
  defineProps<{
    isOpen: boolean
    isLoading?: boolean
    /** Identity class added to the teleported panel (e.g. 'ModalUnpublish') so each
     * confirm modal stays recognisable in the DOM; styling lives on the shared base. */
    rootClass?: string
    title: string
    lead: string
    /** Banner block under the lead. Omit `bannerTitle` to render the lead alone. */
    bannerTheme?:
      'accent' | 'neutral' | 'success' | 'warning' | 'error' | 'info'
    bannerIcon?: IIcons
    bannerTitle?: string
    bannerDescription?: string
    confirmLabel: string
    /** Confirm button theme; omit for the default (primary). Use 'error' for destructive. */
    confirmTheme?: 'primary' | 'secondary' | 'error'
    cancelLabel?: string
  }>(),
  {
    isLoading: false,
    rootClass: '',
    bannerTheme: 'neutral',
    bannerIcon: undefined,
    bannerTitle: '',
    bannerDescription: '',
    cancelLabel: 'Cancel',
  },
)

const emit = defineEmits<{
  close: []
  confirm: []
}>()
</script>

<!--
  Shared confirm modal for the website publish flow (unpublish / delete). Thin
  wrappers (ModalUnpublish, ModalDeleteWebsite) configure the copy + theming.

  UIModal teleports its panel to #AppContainer, so scoped `data-v` styling can't reach
  the content. Style globally, keyed on the `.ModalConfirmAction.UIModalContainer`
  panel (UIModal forwards `class` onto it) so it stays contained. Wrapper identity
  classes ride along via `rootClass`.
-->
<style lang="scss">
.ModalConfirmAction.UIModalContainer {
  --modal-size: 32rem;

  .ModalConfirmAction__body {
    display: flex;
    flex-direction: column;
    gap: var(---fds-global-space-md);
  }

  .ModalConfirmAction__lead {
    margin: 0;
    @include font-body-small-regular;
    color: var(--text-primary);
  }
}
</style>
